// Supply Network panel: pure inheritance resolver for a dealer
// location's "effective plant". Extracted so the orchestrator hook
// stays focused on state + actions.
//
// Resolution order:
//   1. loc.primary_plant_id              -> source "direct"
//   2. linked dealer org's primary plant -> source "dealer_org"
//   3. manufacturer org's default plant  -> source "org_default"
//   4. none                              -> source "none"
//
// "direct" wins over inheritance; clearing a direct assignment
// (set to null) falls back to the chain. The resolver is also used to
// decide whether a per-row reassignment needs a confirm modal —
// changing an inherited value is silent, changing a direct value is
// gated.
import type { MfgPlant } from "@/lib/manufacturingClient";
import type {
  MfgDealerLocation,
  MfgSubscriberDealer,
  MfgSubscriberLocation,
} from "@/lib/manufacturing/dealerTypes";

export type EffectivePlantSource = "direct" | "dealer_org" | "org_default" | "none";

export type EffectivePlant = {
  plantId: string | null;
  source: EffectivePlantSource;
  // Human-readable label of the plant ("CODE · Name") or "(no plant)".
  plantLabel: string;
  // Phrase suitable for an inline subtext, e.g. "inherits from dealer
  // org" or "from this org's default plant". Empty for "direct".
  sourceLabel: string;
};

const NO_PLANT_LABEL = "(no plant)";

function labelFor(plant: MfgPlant | undefined): string {
  if (!plant) return NO_PLANT_LABEL;
  return `${plant.plant_code} · ${plant.name}`;
}

export function defaultPlantOf(plants: ReadonlyArray<MfgPlant>): MfgPlant | null {
  return plants.find((p) => p.is_default) ?? null;
}

export function buildPlantById(
  plants: ReadonlyArray<MfgPlant>
): Map<string, MfgPlant> {
  const map = new Map<string, MfgPlant>();
  for (const p of plants) map.set(p.id, p);
  return map;
}

export function buildDealerOrgPlantId(
  subscriberDealers: ReadonlyArray<MfgSubscriberDealer>
): Map<string, string> {
  // Only includes orgs that actually have a primary_plant_id set.
  const map = new Map<string, string>();
  for (const s of subscriberDealers) {
    if (s.primary_plant_id) map.set(s.dealer_org_id, s.primary_plant_id);
  }
  return map;
}

export function resolveEffectivePlant(
  loc: MfgDealerLocation,
  plantById: Map<string, MfgPlant>,
  dealerOrgPlantId: Map<string, string>,
  defaultPlant: MfgPlant | null
): EffectivePlant {
  if (loc.primary_plant_id) {
    return {
      plantId: loc.primary_plant_id,
      source: "direct",
      plantLabel: labelFor(plantById.get(loc.primary_plant_id)),
      sourceLabel: "",
    };
  }
  if (loc.linked_dealer_org_id) {
    const orgPid = dealerOrgPlantId.get(loc.linked_dealer_org_id);
    if (orgPid) {
      return {
        plantId: orgPid,
        source: "dealer_org",
        plantLabel: labelFor(plantById.get(orgPid)),
        sourceLabel: "inherits from dealer org",
      };
    }
  }
  if (defaultPlant) {
    return {
      plantId: defaultPlant.id,
      source: "org_default",
      plantLabel: labelFor(defaultPlant),
      sourceLabel: "inherits from your default plant",
    };
  }
  return {
    plantId: null,
    source: "none",
    plantLabel: NO_PLANT_LABEL,
    sourceLabel: "no default plant set",
  };
}

// Same resolution chain, but for an app-using subscriber dealer's
// location (MfgSubscriberLocation, backed by public.locations +
// mfg_subscriber_location_settings overlay). Slightly different
// input shape:
//   - The "direct" override lives on the manufacturer's overlay row
//     (loc.primary_plant_id, populated from mfg_subscriber_location_settings).
//   - The "dealer_org" inheritance hop uses loc.subscriber_org_id
//     (always present), unlike MfgDealerLocation.linked_dealer_org_id
//     which is nullable.
export function resolveSubscriberLocationEffectivePlant(
  loc: MfgSubscriberLocation,
  plantById: Map<string, MfgPlant>,
  dealerOrgPlantId: Map<string, string>,
  defaultPlant: MfgPlant | null
): EffectivePlant {
  if (loc.primary_plant_id) {
    return {
      plantId: loc.primary_plant_id,
      source: "direct",
      plantLabel: labelFor(plantById.get(loc.primary_plant_id)),
      sourceLabel: "",
    };
  }
  const orgPid = dealerOrgPlantId.get(loc.subscriber_org_id);
  if (orgPid) {
    return {
      plantId: orgPid,
      source: "dealer_org",
      plantLabel: labelFor(plantById.get(orgPid)),
      sourceLabel: `inherits from ${loc.subscriber_org_name}`,
    };
  }
  if (defaultPlant) {
    return {
      plantId: defaultPlant.id,
      source: "org_default",
      plantLabel: labelFor(defaultPlant),
      sourceLabel: "inherits from your default plant",
    };
  }
  return {
    plantId: null,
    source: "none",
    plantLabel: NO_PLANT_LABEL,
    sourceLabel: "no default plant set",
  };
}
